import React from 'react';

/**
 * The link across to Netru.
 *
 * TechSonar and Netru are separate businesses on purpose. Netru assesses and
 * certifies against frameworks; TechSonar builds and runs the thing being
 * assessed. Keeping them at arm's length is what lets Netru stay objective
 * about work it did not deliver, which is the whole basis of an assurance
 * opinion.
 *
 * That separation is the reason this referral is worth anything, but it is not
 * the pitch, and it is not the opening line.
 *
 * Two earlier versions of this copy got that wrong in different ways. The first
 * opened with "we do not audit it, and we do not certify it", telling the
 * reader what they could not have before telling them what they could. The
 * second opened with what NIS2 does in the UK, which made a directive the
 * subject of the sentence and read as a news bulletin.
 *
 * The rule for this block, and for service copy generally: the first sentence
 * names a capability or an outcome, its subject is "we" or "you", and a
 * regulatory fact earns its place only where it proves we know something a
 * competitor does not.
 *
 * It appears only where a reader has a real reason to want the other half: a
 * page about engineering for a regime that ends in a certificate or a
 * supervisory assessment. Deliberately not in the footer or the nav, because
 * reciprocal links between two owned domains stop reading as a referral and
 * start reading as a scheme once they are everywhere.
 */
export default function NetruPartner({
  heading = 'Need the certification as well?',
  body,
  href = 'https://netru.io',
  linkLabel = 'Netru',
}: {
  heading?: string;
  body: string;
  href?: string;
  linkLabel?: string;
}) {
  return (
    <section className="relative py-16 lg:py-20">
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="max-w-[760px] border border-[#222] bg-[#0a0f14] p-8 lg:p-10">
          <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">
            [Partner]
          </p>
          <h2 className="font-heading text-[clamp(1.4rem,3vw,1.9rem)] text-[#f5f5f8] font-medium leading-tight mb-4">
            {heading}
          </h2>
          <p className="text-[#aaa] text-base leading-relaxed mb-6">{body}</p>
          <a
            href={href}
            className="inline-flex items-center gap-2 text-[#80edfa] text-sm font-medium hover:underline underline-offset-4"
          >
            Visit {linkLabel}
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
